import { EAT } from "@eldrun/sim";
import type { AvatarSignals, Gesture } from "../game/avatar-state.ts";

const GESTURES: readonly Gesture[] = ["golpe", "arco", "comendo", "dano"];

/** Um gesto no roteiro da vitrine: de `from` a `to` (s), com o item na mão dele (opcional). */
export interface ScriptGesture {
  readonly kind: Gesture;
  readonly from: number;
  readonly to: number;
  readonly item?: string;
}

/**
 * Roteiro da vitrine dos avatares (`&andar=&agachado=1&gestos=&duracao=`): os bonecos andam para a
 * frente na velocidade dada, agachados ou não, com os gestos nos intervalos pedidos, pela máquina de
 * estados do jogo (não pelo clipe solto). Para ver os pés no chão e as trocas de gesto quadro a quadro.
 */
export interface AvatarScript {
  /** m/s para a frente (o −Z do boneco). */
  readonly speed: number;
  readonly crouch: boolean;
  readonly gestures: readonly ScriptGesture[];
  /** Duração (s): o boneco anda de +v·L/2 a −v·L/2 e recomeça. */
  readonly length: number;
}

const finite = (value: string | null | undefined): number | undefined => {
  if (value === null || value === undefined || value.trim() === "") return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
};

/**
 * Lê o roteiro dos parâmetros (`andar=2`, `agachado=1`, `gestos=golpe:0.2:0.7:espada-prata,arco:0.7:2:arco-galho`,
 * `duracao=3`); `undefined` sem `andar` nem `gestos` (a vitrine toca um clipe solto, como antes).
 */
export function parseAvatarScript(params: URLSearchParams): AvatarScript | undefined {
  const speed = finite(params.get("andar"));
  const raw = params.get("gestos");
  if (speed === undefined && !raw) return undefined;
  const gestures: ScriptGesture[] = [];
  for (const part of (raw ?? "").split(",")) {
    const [kind, from, to, item] = part.split(":");
    const a = finite(from);
    const b = finite(to);
    if (!GESTURES.includes(kind as Gesture) || a === undefined || b === undefined || b <= a) continue;
    gestures.push({ kind: kind as Gesture, from: a, to: b, ...(item && { item }) });
  }
  const end = gestures.reduce((m, g) => Math.max(m, g.to), 0);
  return { speed: Math.max(0, speed ?? 0), crouch: params.get("agachado") === "1", gestures, length: finite(params.get("duracao")) ?? Math.max(2, end + 0.5) };
}

/** Os sinais do corpo no instante `t` do roteiro (no chão, vivo). */
export function scriptSignals(script: AvatarScript, t: number): AvatarSignals {
  const active = (kind: Gesture) => script.gestures.find((g) => g.kind === kind && g.from <= t && t < g.to);
  const swing = active("golpe");
  const eat = active("comendo");
  const hurt = script.gestures.filter((g) => g.kind === "dano" && g.from <= t).at(-1);
  return {
    speed: script.speed,
    vy: 0,
    grounded: true,
    crouching: script.crouch,
    swimming: false,
    sliding: false,
    dead: false,
    swing: swing ? Math.max(1e-3, (t - swing.from) / (swing.to - swing.from)) : 0,
    drawing: active("arco") !== undefined,
    eating: eat !== undefined,
    bites: eat ? Math.floor((t - eat.from) / EAT.seconds) : 0,
    hurtAgo: hurt ? t - hurt.from : Infinity,
    revivedAgo: Infinity,
  };
}

/** O item na mão em `t`: o do último gesto que já começou e diz qual, ou `fallback`. */
export function scriptItem(script: AvatarScript, t: number, fallback: string): string {
  return script.gestures.filter((g) => g.from <= t && g.item !== undefined).at(-1)?.item ?? fallback;
}

/** Onde o boneco está no eixo da frente (z) em `t`: anda de +v·L/2 a −v·L/2 e recomeça. */
export function scriptZ(script: AvatarScript, t: number): number {
  const loop = t - Math.floor(t / script.length) * script.length;
  return script.speed * (script.length / 2 - loop);
}
